import { useState, useEffect } from 'react';
import { Heart, Trash2, Edit2, Package, Star, Sparkles, Search, Filter, X, Check, Gem } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface CollectionCrystal {
  id: string;
  crystal_name: string;
  image_url: string | null;
  identification_data: any;
  notes: string | null;
  is_owned: boolean;
  is_wishlist: boolean;
  is_favorite: boolean;
  identified_at: string;
}

export function CrystalCollection() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'owned' | 'wishlist' | 'favorites'>('all');
  const [selectedCrystal, setSelectedCrystal] = useState<CollectionCrystal | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();

  const { data: crystals = [], isLoading, error } = useQuery({
    queryKey: ['crystal-collection'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('user_crystal_collection')
        .select('*')
        .eq('user_id', user.id)
        .order('identified_at', { ascending: false });
      
      if (error) throw error;
      return data as CollectionCrystal[];
    }
  });

  const updateCrystalMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CollectionCrystal> }) => {
      const { error } = await supabase
        .from('user_crystal_collection')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crystal-collection'] });
      toast.success('Crystal updated');
    },
    onError: () => {
      toast.error('Failed to update crystal');
    }
  });

  const deleteCrystalMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('user_crystal_collection')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crystal-collection'] });
      setSelectedCrystal(null);
      toast.success('Crystal removed from collection');
    },
    onError: () => {
      toast.error('Failed to remove crystal');
    }
  });

  const toggleFavorite = (crystal: CollectionCrystal) => {
    updateCrystalMutation.mutate({
      id: crystal.id,
      updates: { is_favorite: !crystal.is_favorite }
    });
  };

  const toggleOwned = (crystal: CollectionCrystal) => {
    updateCrystalMutation.mutate({
      id: crystal.id,
      updates: { 
        is_owned: !crystal.is_owned,
        is_wishlist: crystal.is_owned ? crystal.is_wishlist : false
      }
    });
  };

  const toggleWishlist = (crystal: CollectionCrystal) => {
    updateCrystalMutation.mutate({
      id: crystal.id,
      updates: { 
        is_wishlist: !crystal.is_wishlist,
        is_owned: crystal.is_wishlist ? crystal.is_owned : false
      }
    });
  };

  const saveNotes = () => {
    if (!selectedCrystal) return;
    updateCrystalMutation.mutate({
      id: selectedCrystal.id,
      updates: { notes: editNotes }
    });
    setIsEditing(false);
  };

  const filteredCrystals = crystals.filter(crystal => {
    const matchesSearch = crystal.crystal_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = 
      activeFilter === 'all' ||
      (activeFilter === 'owned' && crystal.is_owned) ||
      (activeFilter === 'wishlist' && crystal.is_wishlist) ||
      (activeFilter === 'favorites' && crystal.is_favorite);
    
    return matchesSearch && matchesFilter;
  });

  const stats = {
    total: crystals.length,
    owned: crystals.filter(c => c.is_owned).length,
    wishlist: crystals.filter(c => c.is_wishlist).length,
    favorites: crystals.filter(c => c.is_favorite).length
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Sparkles className="h-6 w-6 animate-pulse text-primary" />
        <span className="ml-2 text-muted-foreground">Loading your collection...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="bg-destructive/10 border-destructive/30">
        <CardContent className="p-6 text-center">
          <p className="text-destructive">Failed to load collection. Please try again.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-3 text-center">
            <Gem className="h-5 w-5 mx-auto mb-1 text-primary" />
            <p className="text-lg font-semibold text-primary">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
          <CardContent className="p-3 text-center">
            <Package className="h-5 w-5 mx-auto mb-1 text-accent" />
            <p className="text-lg font-semibold text-accent">{stats.owned}</p>
            <p className="text-xs text-muted-foreground">Owned</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5 border-secondary/20">
          <CardContent className="p-3 text-center">
            <Star className="h-5 w-5 mx-auto mb-1 text-secondary" />
            <p className="text-lg font-semibold text-secondary">{stats.wishlist}</p>
            <p className="text-xs text-muted-foreground">Wishlist</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-pink-500/10 to-pink-500/5 border-pink-500/20">
          <CardContent className="p-3 text-center">
            <Heart className="h-5 w-5 mx-auto mb-1 text-pink-500" />
            <p className="text-lg font-semibold text-pink-500">{stats.favorites}</p>
            <p className="text-xs text-muted-foreground">Favourites</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search crystals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Filter Tabs */}
      <Tabs value={activeFilter} onValueChange={(v) => setActiveFilter(v as any)}>
        <TabsList className="w-full">
          <TabsTrigger value="all" className="flex-1">All</TabsTrigger>
          <TabsTrigger value="owned" className="flex-1">Owned</TabsTrigger>
          <TabsTrigger value="wishlist" className="flex-1">Wishlist</TabsTrigger>
          <TabsTrigger value="favorites" className="flex-1">Favorites</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Crystal Grid */}
      {filteredCrystals.length === 0 ? (
        <Card className="bg-muted/30">
          <CardContent className="p-8 text-center">
            <Gem className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="font-medium text-foreground mb-2">No crystals yet</h3>
            <p className="text-sm text-muted-foreground">
              {activeFilter === 'all' 
                ? "Identify a crystal and save it to start your collection!"
                : `No crystals in your ${activeFilter} list.`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {filteredCrystals.map((crystal) => (
            <Card 
              key={crystal.id}
              className="cursor-pointer hover:shadow-lg transition-all bg-card/80 backdrop-blur-sm border-border/50 overflow-hidden"
              onClick={() => {
                setSelectedCrystal(crystal);
                setEditNotes(crystal.notes || '');
              }}
            >
              <div className="relative aspect-square bg-gradient-to-br from-primary/10 to-secondary/10">
                {crystal.image_url ? (
                  <img 
                    src={crystal.image_url} 
                    alt={crystal.crystal_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gem className="h-12 w-12 text-primary/30" />
                  </div>
                )}
                
                {/* Status Icons */}
                <div className="absolute top-2 right-2 flex flex-col gap-1">
                  {crystal.is_favorite && (
                    <div className="p-1 bg-pink-500 rounded-full">
                      <Heart className="h-3 w-3 text-white fill-white" />
                    </div>
                  )}
                  {crystal.is_owned && (
                    <div className="p-1 bg-accent rounded-full">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                  )}
                  {crystal.is_wishlist && (
                    <div className="p-1 bg-secondary rounded-full">
                      <Star className="h-3 w-3 text-white fill-white" />
                    </div>
                  )}
                </div>
              </div>
              
              <CardContent className="p-3">
                <h4 className="font-medium text-sm text-foreground truncate">{crystal.crystal_name}</h4>
                <p className="text-xs text-muted-foreground">
                  {new Date(crystal.identified_at).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Crystal Detail Dialog */}
      <Dialog open={!!selectedCrystal} onOpenChange={(open) => !open && setSelectedCrystal(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedCrystal && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Gem className="h-5 w-5 text-primary" />
                  {selectedCrystal.crystal_name}
                </DialogTitle>
                <DialogDescription>
                  Identified on {new Date(selectedCrystal.identified_at).toLocaleDateString()}
                </DialogDescription>
              </DialogHeader>

              {/* Image */}
              {selectedCrystal.image_url && (
                <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                  <img 
                    src={selectedCrystal.image_url}
                    alt={selectedCrystal.crystal_name}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex gap-2">
                <Button
                  variant={selectedCrystal.is_favorite ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleFavorite(selectedCrystal)}
                  className="flex-1"
                >
                  <Heart className={`h-4 w-4 mr-1 ${selectedCrystal.is_favorite ? 'fill-current' : ''}`} />
                  Favourite
                </Button>
                <Button
                  variant={selectedCrystal.is_owned ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleOwned(selectedCrystal)}
                  className="flex-1"
                >
                  <Package className="h-4 w-4 mr-1" />
                  Owned
                </Button>
                <Button
                  variant={selectedCrystal.is_wishlist ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleWishlist(selectedCrystal)}
                  className="flex-1"
                >
                  <Star className={`h-4 w-4 mr-1 ${selectedCrystal.is_wishlist ? 'fill-current' : ''}`} />
                  Wishlist
                </Button>
              </div>

              {/* Properties from identification */}
              {selectedCrystal.identification_data && (
                <div className="space-y-3">
                  {selectedCrystal.identification_data.chakras && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Chakras</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedCrystal.identification_data.chakras.map((chakra: string, i: number) => (
                          <Badge key={i} variant="secondary" className="text-xs">{chakra}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {selectedCrystal.identification_data.healingProperties && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Healing Properties</p>
                      <ul className="text-sm text-foreground/80 space-y-1">
                        {selectedCrystal.identification_data.healingProperties.slice(0, 3).map((prop: string, i: number) => (
                          <li key={i} className="text-xs">• {prop}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Notes</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    {isEditing ? 'Cancel' : 'Edit'}
                  </Button>
                </div>
                
                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Add your notes about this crystal..."
                      className="min-h-[100px]"
                    />
                    <Button size="sm" onClick={saveNotes}>Save Notes</Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                    {selectedCrystal.notes || 'No notes yet. Click Edit to add some!'}
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteCrystalMutation.mutate(selectedCrystal.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove from Collection
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
